import React, { useEffect, useState, useRef } from "react"
import Card from "../../../../components/atoms/Card.atom"
import { ReactComponent as CalendarTimeIcon } from "../../../../design/assests/images/icons/time.svg"
import { getActiveProjects } from "../../../../services/project.service"
import {
  getAllFollowersMyFollowedTasks,
  getLazyUserFollowedTasks,
} from "../../../../services/followedTask.service"
import {
  FOLLOWED_TASK_ROW_HEIGHT,
  DEFAULT_FOLLOWED_TASKS_FILTER_CONFIG,
} from "../../../../consts/modules/pendingTasks.consts"
import FolowedTasksControls from "./FollowedTasksControls.controller"
import MyFollowedTasksDataTable from "../../components/MyFollowedTasksDataTable.compound"

const FollowedTasksContent = () => {
  // Refresh states
  const [refresh, setRefresh] = useState(false)
  const triggerRefresh = () => setRefresh(!refresh)

  const [loading, setLoading] = useState(true)
  const [followedTasks, setFollowedTasks] = useState([])
  const [activeTeamMembers, setActiveTeamMembers] = useState([])

  // Controls states
  const [sortConfig, setSortConfig] = useState({
    label: "Por fecha de creación",
    icon: <CalendarTimeIcon className="clr-text p-menuitem-icon svg" />,
    field: "taskCreatedAt",
    order: "desc",
  })
  const [filterConfig, setFilterConfig] = useState(
    DEFAULT_FOLLOWED_TASKS_FILTER_CONFIG
  )
  const [allProjects, setAllProjects] = useState([])
  const [selectedProjects, setSelectedProjects] = useState([])
  const [allTeamMembers, setAllTeamMembers] = useState([])
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([])
  const [selectedStates, setSelectedStates] = useState([])

  // References
  const tableRef = useRef()
  const lastFirst = useRef(0)
  const startScrollIndex = useRef(0)
  const endScrollIndex = useRef(0)
  const tableHeight = useRef(0)

  useEffect(() => {
    setLoading(true)
    Promise.all([
      gettingActiveProjectsData(),
      gettingUserFollowedTasks(),
      gettingAllFollowersMyFollowedTasks(),
    ]).finally(() => {
      setLoading(false)
    })
  }, [refresh, sortConfig, filterConfig])

  useEffect(() => {
    setRestOfFilters()
  }, [selectedTeamMembers, selectedProjects, selectedStates])

  useEffect(() => {
    tableHeight.current = calculateTableHeight(followedTasks.length || 5)
  }, [followedTasks.length])

  const calculateTableHeight = (numberOfRows) => {
    return numberOfRows * FOLLOWED_TASK_ROW_HEIGHT
  }

  const generateEmptyData = (numberOfRows) => {
    return Array.from({ length: numberOfRows }).map(() => ({
      name: "Something",
      projectId: 1,
      projectName: "EMPTY",
      clientCode: "Client Code",
      projectTagName: "Project Tag",
      limitDate: "2023-10-10",
      followerIds: [],
      estimatedTime: 0,
      comment: "",
      activeTeamMembers: [],
      isFinished: 0,
      isArchived: false,
      userId: 1,
      userName: "User Name",
      userLastName: "User Last Name",
      userTagName: "User Tag",
      userColor: "#000000",
      finishService: () => {},
    }))
  }

  const setGlobalFilter = (value) => {
    setFilterConfig((prev) => ({
      ...prev,
      globalFilter: value,
    }))
  }

  const setRestOfFilters = () => {
    setFilterConfig((prev) => ({
      ...prev,
      followerIds: selectedTeamMembers,
      projectIds: selectedProjects,
      stateIds: selectedStates,
    }))
  }

  const gettingActiveProjectsData = async () => {
    const response = await getActiveProjects()
    if (response.success) {
      setAllProjects(
        response.result.map((el) => ({
          ...el,
        }))
      )
    }
  }

  const gettingUserFollowedTasks = async (first = 0, last = 1000) => {
    setLoading(true)
    try {
      const response = await getLazyUserFollowedTasks(
        filterConfig,
        {
          label: sortConfig.label,
          field: sortConfig.field,
          order: sortConfig.order,
        },
        first,
        last
      )
      if (response.success) {
        const wholeData = generateEmptyData(
          response?.result?.[0]?.totalElements || 5
        )
        Array.prototype.splice.apply(wholeData, [
          ...[first, last],
          ...response.result,
        ])
        setFollowedTasks(wholeData)
      }
    } catch (error) {
      console.error(error)
    }
    setLoading(false)
  }

  const gettingAllFollowersMyFollowedTasks = async () => {
    const response = await getAllFollowersMyFollowedTasks()
    if (response.success) {
      const allTeamMembers = response.result
      setActiveTeamMembers(
        allTeamMembers.map((el) => {
          return {
            id: el.followerId,
            nameTag: el.nameTag,
            name: el.name,
            lastName: el.lastName,
            active: el.active,
            color: el.color,
          }
        })
      )
      setAllTeamMembers(
        allTeamMembers
          .reduce(
            (acc, el) => {
              if (el.isActive) {
                acc[0].items.push(el)
              } else {
                acc[1].items.push(el)
              }
              return acc
            },
            [
              {
                active: true,
                items: [],
              },
              {
                active: false,
                items: [],
              },
            ]
          )
          .filter((el) => el.items.length > 0)
      )
    }
  }

  const searchResultTemplate = () => {
    return (
      <div
        className={`flex ${loading ? "align-items-center" : "align-items-end"} font-bold text-md mb-3 ${loading ? "opacity-50" : ""} gap-1`}
      >
        {loading ?
          <i className="pi pi-spin pi-spinner text-md m-0 line-height-1"></i>
        : `${followedTasks.length}`}
        <span>resultados de búsqueda:</span>
      </div>
    )
  }

  const onLazyLoad = async (event) => {
    const first = event?.first
    const last = event?.last + 25
    console.log("onLazyLoad", event, lastFirst.current)
    const loadedTasks = followedTasks.slice(0, first)
    const hasMissingPreviousData = loadedTasks.some(
      (task) => task.projectName === "EMPTY"
    )
    if (first > last) {
      return
    }

    if (
      first >= lastFirst.current ||
      (first < lastFirst.current && hasMissingPreviousData)
    ) {
      if (loading) return

      if (first > last) {
        return
      }
      try {
        const response = await getLazyUserFollowedTasks(
          filterConfig,
          {
            label: sortConfig.label,
            field: sortConfig.field,
            order: sortConfig.order,
          },
          first,
          last
        )

        if (response.success) {
          Array.prototype.splice
            .apply(followedTasks, [
              first,
              response.result.length,
              ...response.result,
            ])
            .slice(first, response.result?.[0]?.totalElements || 75)
          setFollowedTasks(followedTasks)
        }
      } catch (error) {
        console.error("Error al cargar tareas seguidas:", error)
      }
      lastFirst.current = first
    } else {
      const loadedTasks = followedTasks.slice(0, lastFirst.current)
      setFollowedTasks((prev) => {
        const newData = [...prev]
        newData.splice(0, lastFirst.current, ...loadedTasks)
        return newData
      })
    }
  }

  const onScrollIndexChange = ({ first }) => {
    const tbodyEl = tableRef.current?.getTable()?.querySelector("tbody") ?? null

    if (tbodyEl === null) {
      return
    }
    tbodyEl.style.transform = `translate3d(0px, ${startScrollIndex.current * FOLLOWED_TASK_ROW_HEIGHT}px, 0px)`
    if (endScrollIndex.current !== startScrollIndex.current) {
      endScrollIndex.current = first
      return
    }

    endScrollIndex.current = first

    const observer = new MutationObserver(() => {
      observer.disconnect()
      startScrollIndex.current = endScrollIndex.current
      tbodyEl.style.transform = `translate3d(0px, ${endScrollIndex.current * FOLLOWED_TASK_ROW_HEIGHT}px, 0px)`
    })
    observer.observe(tbodyEl, { childList: true })
  }

  const hasFilters = () => {
    if (filterConfig.globalFilter !== "") return true
    if (selectedTeamMembers.length > 0) return true
    if (selectedProjects.length > 0) return true
    if (selectedStates.length > 0) return true
    return false
  }

  document.documentElement.style.setProperty(
    "--fill-drop-area-task-list",
    "20px"
  )
  return (
    <Card className="pending-task-list-wrapper followed-tasks-wrapper shadow-2 min-h-0 overflow-hidden">
      <FolowedTasksControls
        sortConfig={sortConfig}
        setSortConfig={setSortConfig}
        globalFilter={filterConfig.globalFilter}
        setGlobalFilter={setGlobalFilter}
        projects={allProjects}
        selectedProjects={selectedProjects}
        setSelectedProjects={setSelectedProjects}
        teamMembers={allTeamMembers}
        selectedTeamMembers={selectedTeamMembers}
        setSelectedTeamMembers={setSelectedTeamMembers}
        loadingData={loading}
        selectedStates={selectedStates}
        setSelectedStates={setSelectedStates}
      />
      {filterConfig.globalFilter !== "" && searchResultTemplate()}
      <div className="pending-task-list-content flex flex-column gap-3">
        <>
          <MyFollowedTasksDataTable
            ref={tableRef}
            tasks={followedTasks}
            activeTeamMembers={activeTeamMembers}
            showLoading={true}
            loading={loading}
            triggerRefresh={triggerRefresh}
            height={tableHeight.current}
            virtualScrollerOptions={{
              className: "w-full",
              itemSize: FOLLOWED_TASK_ROW_HEIGHT,
              numToleratedItems: 20,
              lazy: true,
              onLazyLoad,
              onScrollIndexChange,
              items: followedTasks,
            }}
            emptyCustomMessage={
              <CustomEmptyMessage
                emptyMessageTitle={
                  hasFilters() ? "Sin información" : "Todo listo"
                }
                emptyMessageContent={
                  hasFilters() ?
                    "Intenta aplicar otros filtros."
                  : "Sin pendientes por ahora, disfruta el momento mientras dure."
                }
                height={228}
              />
            }
          />
        </>
      </div>
    </Card>
  )
}

const CustomEmptyMessage = ({
  emptyMessageTitle = "Sin información",
  emptyMessageContent = "Intenta aplicar otros filtros",
  width = "auto",
  height = "auto",
}) => {
  return (
    <div
      className="empty-data-historical-pending-tasks-message text-center flex flex-column justify-content-center"
      style={{ height: height, width: width }}
    >
      <i className="pi pi-folder-open"></i>
      <h3 className="title font-cairo">{emptyMessageTitle}</h3>
      <p className="message">{emptyMessageContent}</p>
    </div>
  )
}

export default FollowedTasksContent
